import styled from "@emotion/styled";
import { useCallback, type CSSProperties } from "react";

import {
  Form,
  FormField,
  FormikErrorMessages,
  FormikForm,
  PropertiesPanelSection,
  SkiliticsDuotoneIcon,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useEditingCss } from "./EditingCssPropertiesContext";
import { FieldCssInput } from "./FieldCssInput";

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 5px;
  margin-bottom: 10px;
`;

type FormValues = Pick<
  CSSProperties,
  "paddingLeft" | "paddingRight" | "paddingTop" | "paddingBottom" | "rowGap" | "columnGap"
>;

export const PaddingProperties = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const { cssProperties, onCssPropertiesChange } = useEditingCss();

  const handleSubmit = useCallback((values: FormValues) => onCssPropertiesChange(values), [onCssPropertiesChange]);

  const { paddingLeft = "", paddingRight = "", paddingTop = "", paddingBottom = "" } = cssProperties;

  const initialValues: FormValues = { paddingLeft, paddingRight, paddingTop, paddingBottom };

  return (
    <PropertiesPanelSection title={formatMessage("sectionHeading")}>
      <FormikForm<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitOnBlur
        dataTest="padding-properties-form"
        validateOnChange={false}
      >
        <Form>
          <Row>
            <FormField<FormValues> name="paddingTop">
              <FieldCssInput
                placeholder="0px"
                label={<SkiliticsDuotoneIcon icon="side-padding" rotation={90} />}
                tooltip={formatMessage("paddingTop")}
              />
            </FormField>

            <FormField<FormValues> name="paddingLeft">
              <FieldCssInput
                placeholder="0px"
                label={<SkiliticsDuotoneIcon icon="side-padding" />}
                tooltip={formatMessage("paddingLeft")}
              />
            </FormField>
            <FormField<FormValues> name="paddingBottom">
              <FieldCssInput
                placeholder="0px"
                label={<SkiliticsDuotoneIcon icon="side-padding" rotation={270} />}
                tooltip={formatMessage("paddingBottom")}
              />
            </FormField>

            <FormField<FormValues> name="paddingRight">
              <FieldCssInput
                placeholder="0px"
                label={<SkiliticsDuotoneIcon icon="side-padding" flip="horizontal" />}
                tooltip={formatMessage("paddingRight")}
              />
            </FormField>
          </Row>
          <FormikErrorMessages />
        </Form>
      </FormikForm>
    </PropertiesPanelSection>
  );
};

const formatStrings = {
  sectionHeading: {
    en: "Padding",
  },
  paddingLeft: {
    en: "Left Padding",
  },
  paddingRight: {
    en: "Right Padding",
  },
  paddingTop: {
    en: "Top Padding",
  },
  paddingBottom: {
    en: "Bottom Padding",
  },
} satisfies FormatMessages;
